import React from 'react';
import { Card } from 'react-bootstrap';
import {
    StatusBadge,
    ProgressBarWrapper
} from '../styles/BillingStyledComponents';

/**
 * 청구 페이지 상단의 작은 요약 카드(이번달 비용 / 예상 금액 / 지난달 비용).
 *
 * 이전 라운드까지 isFinal 분기로 최종 요금 패널 역할도 했지만,
 * 이번 라운드에서 그 역할은 BillingPanel로 통합되어 simple/forecast 모드만 남음.
 */
const BillingSummary = ({
    title,
    amount,
    comparisonAmount,
    progressPercentage,
    selectedMonth,
    availableMonths = [],
    showProgress = false,
    isForecast = false,
    isSamePeriodComparison = false,
    decimalPlaces = 2,
    roundingMethod = 'round'
}) => {
    const percentageChange = comparisonAmount
        ? ((amount - comparisonAmount) / comparisonAmount) * 100
        : 0;

    const formatPercentage = (value) => {
        if (roundingMethod === 'round') {
            return Number(Math.abs(value)).toFixed(decimalPlaces);
        } else if (roundingMethod === 'floor') {
            const multiplier = Math.pow(10, decimalPlaces);
            return (Math.floor(Math.abs(value) * multiplier) / multiplier).toFixed(decimalPlaces);
        }
        return Number(Math.abs(value)).toFixed(decimalPlaces);
    };

    const parsedMonths = availableMonths.map(opt => {
        if (typeof opt === 'string') {
            const parts = opt.split('_');
            const baseYearMonth = parts[0];
            const isFinalOpt = parts.includes('final');
            const finalizedDate = isFinalOpt && parts.length > 2 ? parts[2].split('T')[0] : null;
            const [year, month] = baseYearMonth.split('-').map(Number);
            return { year, month, value: opt, isFinal: isFinalOpt, finalizedDate };
        }
        return { ...opt, value: `${opt.year}-${String(opt.month).padStart(2, '0')}` };
    });

    const getCurrentMonthProgress = () => {
        const now = new Date();
        let year, month;
        if (selectedMonth) {
            const baseYearMonth = selectedMonth.replace('_final', '');
            [year, month] = baseYearMonth.split('-').map(Number);
        } else {
            year = now.getFullYear();
            month = now.getMonth() + 1;
        }
        const lastDay = new Date(year, month, 0).getDate();
        const isCurrentMonth = now.getFullYear() === year && (now.getMonth() + 1) === month;
        const yesterday = isCurrentMonth ? Math.max(now.getDate() - 1, 1) : lastDay;
        const effectiveDay = isCurrentMonth ? Math.min(yesterday, lastDay) : lastDay;
        return { day: effectiveDay, lastDay, percentage: (effectiveDay / lastDay) * 100 };
    };

    const monthProgress = getCurrentMonthProgress();

    return (
        <Card className="h-100" style={{ border: 'none' }}>
            <Card.Header style={{ minHeight: '73px', padding: '12px 16px', background: 'transparent', borderBottom: 'none' }}>
                <h5 style={{ fontWeight: 700, color: '#1e293b', margin: 0 }}>{title}</h5>
            </Card.Header>
            <Card.Body className={isForecast ? 'd-flex flex-column justify-content-between text-center' : ''}>
                {!isForecast && (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a' }}>
                                ₩{amount?.toLocaleString() || '0'}
                            </span>
                        </div>
                        {comparisonAmount !== undefined && (
                            <div style={{
                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                padding: '3px 10px', borderRadius: '6px',
                                background: percentageChange > 0 ? '#fef2f2' : '#f0fdf4',
                                color: percentageChange > 0 ? '#ef4444' : '#10b981',
                                fontWeight: 700, fontSize: '0.85rem'
                            }}>
                                {percentageChange > 0 ? '▲' : '▼'} {formatPercentage(percentageChange)}%
                                {isSamePeriodComparison && (
                                    <span style={{ fontSize: '0.75rem', fontWeight: 500, color: '#64748b' }}>지난달 동일 기간 대비</span>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {isForecast && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a' }}>
                            ₩{amount?.toLocaleString() || '0'}
                        </span>
                        <StatusBadge className="status-pending" style={{ fontSize: '0.7rem' }}>예상</StatusBadge>
                        {comparisonAmount !== undefined && (
                            <div style={{
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                padding: '2px 8px', borderRadius: '6px',
                                background: percentageChange >= 0 ? '#fef2f2' : '#f0fdf4',
                                color: percentageChange >= 0 ? '#ef4444' : '#10b981',
                                fontWeight: 700, fontSize: '0.8rem'
                            }}>
                                {percentageChange >= 0 ? '▲' : '▼'} {formatPercentage(percentageChange)}%
                            </div>
                        )}
                        <span style={{ color: '#64748b', fontSize: '0.8rem', fontWeight: 500 }}>
                            지난달: ₩{comparisonAmount?.toLocaleString() || '0'}
                        </span>
                    </div>
                )}

                {progressPercentage !== undefined && showProgress && (
                    <div style={{ marginTop: '1rem' }}>
                        <p style={{ margin: 0, textAlign: 'center', fontSize: '0.8rem', color: '#64748b', marginBottom: '0.25rem' }}>
                            {selectedMonth ? (() => {
                                const monthData = parsedMonths.find(m => m.value === selectedMonth);
                                const interimText = monthData?.isFinal ? ` 중간정산${monthData.finalizedDate ? ` (${monthData.finalizedDate})` : ''}` : '';
                                return `${monthData?.year}년 ${monthData?.month}월${interimText} `;
                            })() : '이번달 '}
                            <span style={{ color: '#334155', fontWeight: 600 }}>{monthProgress.day}일</span> / {monthProgress.lastDay}일
                        </p>
                        <ProgressBarWrapper>
                            <div className="progress-fill" style={{ width: `${monthProgress.percentage}%` }}>
                                <span className="progress-label">{monthProgress.day}일</span>
                            </div>
                        </ProgressBarWrapper>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

export default BillingSummary;
